import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { useUserStore, type SubscriptionCard } from '@/stores/user'
import allPlusIbisImg from '@/assets/images/my-subscription/all-plus-ibis.png'
import allPlusVoyageurImg from '@/assets/images/my-subscription/all-plus-voyageur.png'
import allPlusExplorerImg from '@/assets/images/my-subscription/all-plus-explorer.png'
import { toLocaleFormat, diffNow } from '@/helpers/date-helpers'
import { IconDanger, IconSuccess, IconInfo } from '@accor/icons'
import { SUBSCRIPTION_NAME, ALL_PLUS_SUBSCRIPTION_STATUS } from '@/constants/subscriptions'
import { type BadgeVariation } from '@accor/ads-components'
import { useRouter } from 'vue-router'

export const useAllPlusSubscription = () => {
  const { t } = useI18n()
  const userStore = useUserStore()
  const router = useRouter()
  const { lastActiveSubscriptionCards } = storeToRefs(userStore)
  const { isAllPlusExplorer, isAllPlusIbis, isAllPlusVoyageur } = userStore

  const isEndDateWithin30DaysBeforeToday = (endDate: string) =>
    diffNow(endDate) >= -30 && diffNow(endDate) <= 0
  const isEndDateWithin60DaysAfterToday = (endDate: string) =>
    diffNow(endDate) > 0 && diffNow(endDate) <= 60

  const getAllPlusSubscriptionStatus = (subscription: SubscriptionCard) => {
    if (isEndDateWithin30DaysBeforeToday(subscription.endDate))
      return ALL_PLUS_SUBSCRIPTION_STATUS.EXPIRES_SOON
    if (isEndDateWithin60DaysAfterToday(subscription.endDate))
      return ALL_PLUS_SUBSCRIPTION_STATUS.EXPIRED
    return ALL_PLUS_SUBSCRIPTION_STATUS.ACTIVE
  }

  const allPlusBadgeBySubscriptionForHub = (subscription: SubscriptionCard) => {
    const subscriptionStatus = getAllPlusSubscriptionStatus(subscription)
    const badgeByStatus = {
      ACTIVE: {
        variation: 'success' as BadgeVariation,
        text: t('common.tag.active'),
        prependIcon: IconSuccess
      },
      EXPIRES_SOON: {
        variation: 'warning' as BadgeVariation,
        text: t('common.tag.willExpireSoon'),
        prependIcon: IconDanger
      },
      EXPIRED: {
        variation: 'danger' as BadgeVariation,
        text: t('subscription.hub.tag.expired'),
        prependIcon: IconDanger
      }
    }
    return badgeByStatus[subscriptionStatus]
  }

  const allPlusBadgeBySubscriptionForCardBlock = (subscription: SubscriptionCard) => {
    const subscriptionStatus = getAllPlusSubscriptionStatus(subscription)
    const badgeByStatus = {
      ACTIVE: {
        prependIcon: IconInfo,
        variation: 'loyalty' as BadgeVariation,
        text: t('validity.date', { date: toLocaleFormat(subscription.endDate) })
      },
      EXPIRES_SOON: {
        prependIcon: IconDanger,
        variation: 'warning' as BadgeVariation,
        text: t('subscription.tag.expireSoon', {
          expirationDate: toLocaleFormat(subscription.endDate)
        })
      },
      EXPIRED: {
        prependIcon: IconDanger,
        variation: 'danger' as BadgeVariation,
        text: t('subscription.tag.alreadyExpired', {
          expirationDate: toLocaleFormat(subscription.endDate)
        })
      }
    }
    return badgeByStatus[subscriptionStatus]
  }

  const getAllPlusSubscriptionDetails = (subscription: SubscriptionCard) => {
    return {
      title: subscription.cardProduct?.productLabel,
      endValidityDate: toLocaleFormat(subscription.endDate)
    }
  }

  const getUrl = (name: SUBSCRIPTION_NAME) =>
    router.resolve({
      name: 'subscription-card-details',
      params: {
        cardName: name
      }
    }).href

  const getAllPlusUrlAndImage = (subscription: SubscriptionCard) => {
    if (isAllPlusExplorer(subscription)) {
      return {
        image: allPlusExplorerImg,
        url: getUrl(SUBSCRIPTION_NAME.ALLPLUS_EXPLORER)
      }
    }

    if (isAllPlusIbis(subscription)) {
      return {
        image: allPlusIbisImg,
        url: getUrl(SUBSCRIPTION_NAME.ALLPLUS_IBIS)
      }
    }

    if (isAllPlusVoyageur(subscription)) {
      return {
        image: allPlusVoyageurImg,
        url: getUrl(SUBSCRIPTION_NAME.ALLPLUS_VOYAGEUR)
      }
    }

    return undefined
  }

  const getSubscriptionName = (subscription: SubscriptionCard) => {
    if (isAllPlusExplorer(subscription)) return SUBSCRIPTION_NAME.ALLPLUS_EXPLORER
    if (isAllPlusIbis(subscription)) return SUBSCRIPTION_NAME.ALLPLUS_IBIS
    if (isAllPlusVoyageur(subscription)) return SUBSCRIPTION_NAME.ALLPLUS_VOYAGEUR
    return undefined
  }

  const allPlusSubscriptionCardPropsInHub = computed(() => {
    return lastActiveSubscriptionCards.value?.map((subscription: SubscriptionCard) => ({
      badge: allPlusBadgeBySubscriptionForHub(subscription),
      type: getSubscriptionName(subscription),
      ...getAllPlusSubscriptionDetails(subscription),
      ...getAllPlusUrlAndImage(subscription)
    }))
  })

  const getAllPlusSubscriptionCardPropsInCardBlock = (subscription: SubscriptionCard) => {
    return {
      badge: allPlusBadgeBySubscriptionForCardBlock(subscription),
      ...getAllPlusSubscriptionDetails(subscription)
    }
  }

  return {
    allPlusSubscriptionCardPropsInHub,
    getAllPlusSubscriptionCardPropsInCardBlock,
    getAllPlusSubscriptionStatus
  }
}
